/**
 * @file 学んだ日の暦 (今日を右端にした1行のマスを並べ、学んだ日を塗る)。
 *
 * @what: マスは1日で、今日が右端にくる。出す日数は画面の幅から決め (幅いっぱいに並べ、横スクロールは
 *   作らない)、読み込んだ期間より長くはしない。学んだ日 (model/studyDays.ts の isLearnedDay) のマスを塗り、
 *   今日のマスに枠を付ける。マスを押すと、その日にさわった本の名前が下に出る。
 * @why: 積み重ねを、数字ではなく塗ったマスの並びで見せる (代表の判断、2026-09-13)。連続の日数や合計は
 *   出さない (epic_23 §retention の「連続記録は子に見せない」に従い、おとなにも出さない)。1行にするのは、
 *   週 × 曜日の2次元では狭い画面で横に溢れ、隠れる日ができるためである (代表の判断、2026-09-13)。
 *   「ゼロでなかった日」を肯定する設計 (attainment_v1 の原則) なので、塗らない日に印や色は付けない。
 * @failure: 描画のみ。学んだ日が1日も無くても暦は出す (これから塗られていく場所として)。幅を測れない
 *   ときは STUDY_DAYS_FALLBACK_COUNT 日を出す。
 */

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { StudySource } from "../api/sources";
import { formatLogDate } from "../api/dates";
import {
  STUDY_DAYS_FALLBACK_COUNT,
  STUDY_DAYS_MIN_COUNT,
  dayCountForWidth,
  isLearnedDay,
  studyDayList,
  type StudyDay,
} from "../model/studyDays";
import "./study.css";

export function StudyDaysCalendar({
  days,
  today,
  from,
  sourcesById,
}: {
  days: ReadonlyMap<string, StudyDay>;
  today: string;
  /** 記録を読んだ期間の初日を渡す。ここより前は出さない (塗られない日が並ぶだけになる)。 */
  from: string;
  /** 本だなの資料 (さわった本の名前を引く)。 */
  sourcesById: ReadonlyMap<string, StudySource>;
}) {
  const [selected, setSelected] = useState<string | null>(null);
  const selectedDay = selected !== null ? days.get(selected) : undefined;
  const learnedCount = [...days.values()].filter(isLearnedDay).length;

  // 並べる日数は行の幅から決める。幅が取れないとき (テスト・非表示) は既定の日数にする
  const rowRef = useRef<HTMLDivElement | null>(null);
  const [count, setCount] = useState(STUDY_DAYS_FALLBACK_COUNT);
  const maxCount = Math.max(STUDY_DAYS_MIN_COUNT, daysBetween(from, today) + 1);
  useLayoutEffect(() => {
    const measure = () => {
      const width = rowRef.current?.clientWidth ?? 0;
      if (width > 0) setCount(dayCountForWidth(width, maxCount));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [maxCount]);
  const dates = studyDayList(today, Math.min(count, maxCount));
  // 出す範囲から外れた日を選んだままにしない
  useEffect(() => {
    setSelected((cur) => (cur !== null && !dates.includes(cur) ? null : cur));
    // dates は毎回新しい配列になるので、範囲の端だけを依存にする
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dates[0], today]);

  return (
    <section className="study-days" aria-label="学んだ日">
      <h3 className="study-shelf-section-title">
        学んだ日
        <span className="study-shelf-section-help">
          5分以上読んだ日と、記録・メモ・復習・AI に聞いた日を塗ります
        </span>
      </h3>
      <div
        className="study-days-row"
        role="grid"
        aria-label="学んだ日"
        ref={rowRef}
        style={{ gridTemplateColumns: `repeat(${dates.length}, 1fr)` }}
      >
        {dates.map((date) => {
          const learned = isLearnedDay(days.get(date));
          const isToday = date === today;
          const className = [
            "study-days-cell",
            learned ? "study-days-cell--learned" : "",
            isToday ? "study-days-cell--today" : "",
            selected === date ? "study-days-cell--selected" : "",
          ]
            .filter(Boolean)
            .join(" ");
          const label = `${formatLogDate(date)}${learned ? " 学んだ日" : ""}${isToday ? " (きょう)" : ""}`;
          return (
            <span key={date} role="gridcell" className="study-days-cellwrap">
              <button
                type="button"
                className={className}
                aria-label={label}
                aria-pressed={selected === date}
                onClick={() => setSelected((cur) => (cur === date ? null : date))}
              />
            </span>
          );
        })}
      </div>
      <p className="study-days-range">
        <span>{formatLogDate(dates[0] ?? today, false)}</span>
        <span>きょう</span>
      </p>
      {learnedCount === 0 ? (
        <p className="study-muted study-days-empty">本を開いて読むと、その日のマスが塗られます。</p>
      ) : null}
      {selected !== null ? (
        <div className="study-days-detail" role="status">
          <b>{formatLogDate(selected)}</b>
          <DayTitles day={selectedDay} sourcesById={sourcesById} />
        </div>
      ) : null}
    </section>
  );
}

/** その日にさわった本の名前を並べる。名前が引けなければ、その旨を1行で出す。 */
function DayTitles({
  day,
  sourcesById,
}: {
  day: StudyDay | undefined;
  sourcesById: ReadonlyMap<string, StudySource>;
}) {
  const titles =
    day === undefined
      ? []
      : [...day.sources.keys()]
          .map((id) => sourcesById.get(id)?.title)
          .filter((t): t is string => typeof t === "string");
  if (titles.length === 0) {
    return (
      <span className="study-muted">
        {day !== undefined && isLearnedDay(day) ? "本の記録はありません。" : "この日の記録はありません。"}
      </span>
    );
  }
  return <span className="study-days-titles">{titles.join("、")}</span>;
}

/** 日付の差 (日)。b が a より前なら負になる。 */
function daysBetween(a: string, b: string): number {
  const [ay, am, ad] = a.split("-").map(Number);
  const [by, bm, bd] = b.split("-").map(Number);
  const t0 = Date.UTC(ay ?? 1970, (am ?? 1) - 1, ad ?? 1);
  const t1 = Date.UTC(by ?? 1970, (bm ?? 1) - 1, bd ?? 1);
  return Math.round((t1 - t0) / 86_400_000);
}
